import React from 'react';

import Location from './Location';

import Skeleton from './Skeleton';

import {
  ModuleHeader,
  usePageContext,
  useEditContext,
  EmptyState,
} from '@xingternal/pages-common';
import Error from '@xingternal/pages-warning';

import MapIcon from './mapIcon.svg';

import { useLocationsQuery } from './LocationsQuery.gql-types';

type RenderErrorType = {
  refetch: () => void;
};

type LocationsContainerType = {
  pageSlug: string;
};

const RenderError = ({ refetch }: RenderErrorType) => (
  <div data-testid={'errorContainer'}>
    <Error
      headerText="EP_ERROR_HEADER"
      bodyText="EP_ERROR_BODY"
      buttonText="EP_ERROR_RELOAD_CTA"
      onClick={() => refetch()}
    />
  </div>
);

const LocationsContainer = ({ pageSlug }: LocationsContainerType) => {
  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();

  const { data, loading, error, refetch } = useLocationsQuery({
    variables: { id: pageSlug },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const mainLocation = data?.entityPageLocations?.collection.find(
    (location) => location.highlighted === true
  );

  const showLocationEmptyState =
    !error && !loading && !mainLocation && isEditing;

  if (loading) {
    return (
      <>
        <ModuleHeader headlineCopyKey="EP_LOCATIONS_HEADLINE" />
        <Skeleton />
      </>
    );
  }

  if (showLocationEmptyState) {
    return (
      <>
        <ModuleHeader
          editTestId="EDIT_BUTTON_ON_LOCATIONS"
          headlineCopyKey="EP_LOCATIONS_HEADLINE"
          editTo={`${pageContext.basePath}/${pageSlug}/edit/locations`}
        />
        <EmptyState
          icon={MapIcon}
          buttonProps={{
            to: `/pages/${pageSlug}/edit/locations`,
            copyKey: 'EP_LOCATION_EMPTY_STATE_BUTTON',
          }}
          headlineCopyKey={'EP_LOCATION_EMPTY_STATE_TITLE'}
          textCopyKey={'EP_LOCATION_EMPTY_STATE_TEXT'}
        />
      </>
    );
  }

  if (mainLocation) {
    return (
      <div data-testid="LOCATIONS_MODULE">
        <ModuleHeader
          editTestId="EDIT_BUTTON_ON_LOCATIONS"
          headlineCopyKey="EP_LOCATIONS_HEADLINE"
          editTo={`${pageContext.basePath}/${pageSlug}/edit/locations`}
        />
        <Location
          {...mainLocation}
          trackingModuleType="Main"
        />
      </div>
    );
  }

  return (
    <>
      <ModuleHeader
        editTestId="EDIT_BUTTON_ON_LOCATIONS"
        headlineCopyKey="EP_LOCATIONS_HEADLINE"
        editTo={`${pageContext.basePath}/${pageSlug}/edit/locations`}
      />
      <RenderError refetch={refetch} />
    </>
  );
};

export default LocationsContainer;
